import moment from 'moment'
import { useEffect, useState } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiTitle } from '@elastic/eui'

import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType, PostalCodeTarget } from 'api/entities/MasterCampaignStub'
import { getIntervalUnitLabel } from 'utils/MasterCampaignPlanUtils'
import { getCampaignColor } from '../../new/GanttChart/utils'

interface CampaignCardProps {
  builder: MasterCampaignPlanCampaignBuilder
}

export const CampaignCardOld = ({ builder }: CampaignCardProps) => {
  const [groupedPostalCodes, setGroupedPostalCodes] = useState<{ city: string; postalCodes: PostalCodeTarget[] }[]>([])

  useEffect(() => {
    const grouped: { city: string; postalCodes: PostalCodeTarget[] }[] = []

    builder.zips?.forEach(target => {
      if (!target.name?.includes(',')) {
        return
      }

      const [, city] = target.name.split(',')
      let postalCodeGroup = grouped.find(g => g.city === city)
      if (!grouped.find(g => g.city === city)) {
        postalCodeGroup = { city, postalCodes: [] }
        grouped.push(postalCodeGroup)
      }
      postalCodeGroup?.postalCodes.push(target)
    })

    setGroupedPostalCodes(grouped)
  }, [builder.zips])

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })


  return (
    <EuiFlexGroup>
      <EuiFlexItem>
        <EuiPanel hasShadow={true} paddingSize={'m'} style={{ backgroundColor: '#ede7dd' }}>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiTitle size={'s'}>
                <p>Campaign: {builder.name}</p>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <div
                key={builder.id}
                style={{
                  width: 75,
                  height: 25,
                  background: getCampaignColor(builder),
                  borderRadius: 2,
                  color: 'white',
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'flex'
                }}>
                <span>{builder.campaignType}</span>
              </div>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <span>
                <b>Starts:</b> {moment(builder.startDate).format('MMMM Do YYYY')}
                <br />
                <b>Duration:</b> {builder.intervalDuration} {getIntervalUnitLabel(builder.intervalUnit, builder.intervalDuration ?? 1)} {builder.intervalFrequency && builder.intervalFrequency > 1 ? `(every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)})` : ''}
              </span>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <span>
                <b>Budget:</b> {formatter.format(builder.intervalBudget)}
                {builder.intervalFrequency && builder.intervalFrequency > 1 ? `/every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                {builder.intervalFrequency && builder.intervalFrequency === 1 ? `/${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
              </span>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>{builder.shortSummary}</EuiFlexItem>
          </EuiFlexGroup>

          <EuiFlexGroup>
            <EuiFlexItem grow={4}>
              {groupedPostalCodes.length > 0 && (
                <>
                  <EuiSpacer size={'l'} />
                  <b>Assigned Geo-Targeting</b>
                  <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                  <ul>
                    {groupedPostalCodes.map(({ city, postalCodes }, index) => (
                      <li key={index}>
                        <b>{city}</b>
                        <EuiSpacer size='xs' />
                        {postalCodes.map((postalCode, index) => (
                          <ul key={index}>
                            <li key={index}>{postalCode.code}</li>
                          </ul>
                        ))}
                        <EuiSpacer size='s' />
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </EuiFlexItem>
            <EuiFlexItem grow={6}>
              {(builder.segmentTargets?.length ?? 0) > 0 && (
                <>
                  <EuiSpacer size={'l'} />

                  <b>Applied Audience Segments</b>

                  <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                </>
              )}

              {builder.segmentTargets && (
                <ul>
                  {builder.segmentTargets.map((segment, index) => (
                    <li key={index}>
                      <b>{segment.hierarchy[segment.hierarchy.length - 1]}</b>
                      <EuiSpacer size='xs' />
                      {segment.name}
                      <EuiSpacer size='s' />
                    </li>
                  ))}
                </ul>
              )}
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiPanel>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}
